import ErrorBoundary from '@/components/ErrorBoundary';
import LoadingSpinner from '@/components/LoadingSpinner';
import MultiStepProjectForm from '@/components/MultiStepProjectForm';
import { configurationService } from '@/services/configurationService';
import { invitationService } from '@/services/invitationService';
import { projectService } from '@/services/projectService';
import { ExtendedCreateProjectData } from '@/types';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const CreateProject: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();

  const [initialData, setInitialData] = useState<Partial<ExtendedCreateProjectData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load project data for editing
  useEffect(() => {
    const loadProjectData = async () => {
      if (!projectId) return;

      setIsLoadingInitialData(true);
      setError(null);

      try {
        const response = await projectService.getProject(projectId);
        if (response.success && response.data) {
          // Map Project to ExtendedCreateProjectData format
          const project = response.data;
          const mappedData: Partial<ExtendedCreateProjectData> = {
            name: project.name,
            description: project.description,
            address: project.address || '',
            location: project.location,
            startDate: project.startDate,
            endDate: project.endDate,
            budget: project.budget,
            teamMembers: project.teamMembers?.map(member => member.userId) || [],
            tools: [],
            directories: [],
            customFields: {},
            drawings: [],
            specifications: [],
            schedules: [],
            advanced: {
              region: '',
              language: 'en',
              currency: 'USD',
              isTestProject: false,
            },
          };
          setInitialData(mappedData);
        } else {
          setError(t('projects.errors.failedToLoad'));
        }
      } catch (error) {
        console.error('Failed to load project data:', error);
        setError(t('projects.errors.failedToLoad'));
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    loadProjectData();
  }, [projectId, t]);

  // Preload configurations when component mounts
  useEffect(() => {
    const preloadData = async () => {
      try {
        await configurationService.preloadConfigurations();
      } catch (error) {
        console.error('Failed to preload configurations:', error);
        // Non-critical error, don't block the UI
      }
    };

    preloadData();
  }, []);

  const handleSubmit = async (data: ExtendedCreateProjectData) => {
    setIsLoading(true);
    setError(null);

    try {
      if (projectId) {
        // Update existing project
        const response = await projectService.updateProject(projectId, data);
        if (response.success) {
          navigate(`/dashboard/projects/${projectId}`);
        } else {
          setError(response.message || t('projects.errors.updateFailed'));
        }
      } else {
        // Create new project
        const response = await projectService.createProject(data);
        if (response.success && response.data) {
          const createdProject = response.data;

          // Send team invitations if any
          if (data.detailedTeamMembers && data.detailedTeamMembers.length > 0) {
            try {
              // Filter members that need email invitations (have email but no real userId)
              const membersToInvite = data.detailedTeamMembers.filter(
                member =>
                  member.user?.email && (!member.userId || member.userId.startsWith('temp-user-'))
              );

              if (membersToInvite.length > 0) {
                // Group members by role since invitation API requires same role for all emails
                const membersByRole = membersToInvite.reduce(
                  (groups, member) => {
                    const role = member.role;
                    if (!groups[role]) {
                      groups[role] = [];
                    }
                    groups[role].push(member);
                    return groups;
                  },
                  {} as Record<string, typeof membersToInvite>
                );

                // Send invitations for each role group
                const invitationPromises = Object.entries(membersByRole).map(
                  async ([role, members]) => {
                    try {
                      const emails = members.map(member => member.user!.email!);
                      const permissions = members[0]?.permissions?.map(p =>
                        typeof p === 'string' ? p : p.id
                      ) || ['view_project'];

                      // Create email statuses with name data for new users
                      const emailStatuses = members.reduce(
                        (statuses, member) => {
                          const email = member.user!.email!;
                          statuses[email] = {
                            exists: false, // These are all new users since they have temp IDs
                            userData:
                              member.user?.firstName && member.user?.lastName
                                ? {
                                    firstName: member.user.firstName,
                                    lastName: member.user.lastName,
                                  }
                                : undefined,
                          };
                          return statuses;
                        },
                        {} as Record<string, any>
                      );

                      return await invitationService.sendInvitations({
                        projectId: createdProject.id || createdProject._id || '',
                        emails,
                        role: role as any,
                        permissions,
                        message: `You have been invited to join the project "${data.name}"`,
                        emailStatuses,
                      });
                    } catch (roleError) {
                      console.error(`Failed to send invitations for role ${role}:`, roleError);
                      return { success: false, message: `Failed to send invitations for ${role}` };
                    }
                  }
                );

                const invitationResults = await Promise.allSettled(invitationPromises);
                const successfulInvitations = invitationResults.filter(
                  result => result.status === 'fulfilled' && result.value?.success
                );

                console.log(
                  `${successfulInvitations.length}/${invitationResults.length} invitation groups sent successfully`
                );

                if (successfulInvitations.length === 0) {
                  console.warn('All team invitations failed, but project creation succeeded');
                }
              }
            } catch (invitationError) {
              console.error('Error in invitation process:', invitationError);
              // Don't throw - just log the error and continue with navigation
            }
          }

          // Ensure we have a valid project ID before navigation
          const projectId = createdProject.id || createdProject._id;
          if (projectId) {
            navigate(`/dashboard/projects/${projectId}`);
          } else {
            console.error('No project ID found in response:', createdProject);
            setError('Project created but failed to navigate to project details');
          }
        } else {
          setError(response.message || t('projects.errors.createFailed'));
        }
      }
    } catch (error) {
      console.error('Error submitting project:', error);
      setError(t('projects.errors.unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (projectId) {
      navigate(`/dashboard/projects/${projectId}`);
    } else {
      navigate('/dashboard/projects');
    }
  };

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Project creation error:', error, errorInfo);
    // You could send this to an error tracking service
  };

  if (isLoadingInitialData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t('common.error')}</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {t('common.retry')}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary onError={handleError}>
      <div className="max-w-7xl mx-auto py-6 px-2">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </button>

          <h1 className="text-3xl font-bold text-gray-900">
            {projectId ? t('projects.creation.editTitle') : t('projects.creation.title')}
          </h1>

          <p className="mt-2 text-gray-600">
            {projectId ? t('projects.creation.editSubtitle') : t('projects.creation.subtitle')}
          </p>
        </div>

        {/* Form */}
        <MultiStepProjectForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialData={initialData}
          isLoading={isLoading}
        />
      </div>
    </ErrorBoundary>
  );
};

export default CreateProject;
